import {
  FilledLabelWrapper,
  InputText,
  InputTextWrapper,
  LimitContainer,
  MaterialLabelWrapper,
  MessageWrapper,
  OuterInputWrapper,
  RequiredWrapper,
} from "components/Shared/Input";
import React, { useState } from "react";
import styled, { css, withTheme } from "styled-components";

import FieldError from "components/FieldError";
import PropTypes from "prop-types";
import RevealIcon from "components/icons/eye";
import Spinner from "components/Spinner";
import formatFieldErrors from "lib/format-field-errors";

const FrontWrapper = styled.div`
  display: flex;
  margin: auto 0;
`;

const BackWrapper = styled(FrontWrapper)`
  margin: auto 0.6875rem;
`;

const FrontIconWrapper = styled.div`
  margin: auto 0.5rem auto 0.6875rem;

  ${({ isMaterialInput }) =>
    isMaterialInput &&
    css`
      margin-left: 0;
    `}
`;

const TrailingIconWrapper = styled.div`
  cursor: pointer;
`;

function InputComponent({
  reff,
  toggleEditComponent,
  disabled,
  styleType,
  label,
  bigFont,
  width,
  placeholder,
  meta: { active, success, submitError, error: syncError, touched } = {},
  TrailingIcon,
  LeadingIcon,
  message,
  input: { onBlur, onChange, onFocus, name, value, ...restOfInput },
  inputMode,
  autoComplete,
  id,
  onInput,
  className,
  showRequiredAsterisk,
  readOnly,
  customErrors,
  type,
  inputLimit,
  loading,
  trailingIconOnClick,
  hideLimitCounter,
  theme,
}) {
  const formErrors = formatFieldErrors({
    submitError,
    syncError,
  });
  const errors = customErrors?.concat(formErrors).filter((x) => x !== null);
  const hasError = (touched && errors?.length > 0) || customErrors?.length > 0;
  const enteredTextLength = value ? value.toString().length : 0;
  const isMaterialInput = styleType === "material" || !styleType;
  const [showPassword, setShowPassword] = useState(false);

  return (
    <OuterInputWrapper className={className} disabled={disabled}>
      {(isMaterialInput && (
        <MaterialLabelWrapper
          isActive={active || value?.toString?.()?.length}
          hasLeadingIcon={LeadingIcon}
          htmlFor={id}
          hideLabelForPlaceholder={!!placeholder}
          showRequiredAsterisk={showRequiredAsterisk}
        >
          {label}
        </MaterialLabelWrapper>
      )) || (
        <FilledLabelWrapper
          htmlFor={id}
          styleType={styleType}
          showRequiredAsterisk={showRequiredAsterisk}
        >
          {label}
          {showRequiredAsterisk ? <RequiredWrapper>*</RequiredWrapper> : null}
        </FilledLabelWrapper>
      )}

      <InputTextWrapper
        readOnly={readOnly}
        styleType={styleType || "material"}
        success={success}
        error={hasError}
        width={width}
      >
        <FrontWrapper>
          {LeadingIcon && (
            <FrontIconWrapper isMaterialInput={isMaterialInput}>
              <LeadingIcon color={active ? theme.themeColors.primary : null} />
            </FrontIconWrapper>
          )}
          <InputText
            ref={reff}
            id={id}
            onFocus={onFocus}
            onBlur={onBlur}
            bigFont={bigFont}
            placeholder={placeholder}
            value={value}
            type={(showPassword && "text") || type}
            disabled={disabled}
            onChange={onChange}
            onInput={onInput}
            name={name}
            maxLength={inputLimit}
            inputMode={inputMode}
            autoComplete={autoComplete}
            readOnly={readOnly}
            {...restOfInput}
          />
        </FrontWrapper>
        <BackWrapper onClick={trailingIconOnClick}>
          {loading && <Spinner color="white" overridePositionAbsolute />}
          {inputLimit && !hideLimitCounter && (
            <LimitContainer>{`${enteredTextLength}/${inputLimit}`}</LimitContainer>
          )}
          {TrailingIcon && (
            <TrailingIconWrapper>
              <TrailingIcon />
            </TrailingIconWrapper>
          )}
          {type === "password" ? (
            <TrailingIconWrapper onClick={() => setShowPassword(!showPassword)}>
              <RevealIcon />
            </TrailingIconWrapper>
          ) : undefined}
          {toggleEditComponent ? toggleEditComponent() : undefined}
        </BackWrapper>
      </InputTextWrapper>
      <MessageWrapper success={success}>{message}</MessageWrapper>
      <FieldError show={hasError} errors={errors} />
    </OuterInputWrapper>
  );
}

export default withTheme(InputComponent);

InputComponent.propTypes = {
  reff: PropTypes.any,
  toggleEditComponent: PropTypes.func,
  placeholder: PropTypes.string,
  TrailingIcon: PropTypes.func,
  LeadingIcon: PropTypes.func,
  colorType: PropTypes.oneOf([
    "primary",
    "secondary",
    "danger",
    "warning",
    "success",
  ]),
  width: PropTypes.string,
  styleType: PropTypes.oneOf(["material", "filled"]),
  outlineType: PropTypes.oneOf(["solid", "subtle", "outline"]),
  bigFont: PropTypes.bool,
  meta: PropTypes.shape({
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object,
      PropTypes.string,
    ]),
    submitError: PropTypes.array,
    success: PropTypes.bool,
  }),
  onClick: PropTypes.func,
  message: PropTypes.node,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  loading: PropTypes.bool,
  showPassword: PropTypes.bool,
  inputLimit: PropTypes.number,
  hideLimitCounter: PropTypes.bool,
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  trailingIconOnClick: PropTypes.func,
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      primary: PropTypes.string,
    }),
  }),
  type: PropTypes.string,
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  showRequiredAsterisk: PropTypes.bool,
  readOnly: PropTypes.bool,
  customErrors: PropTypes.array,
  onInput: PropTypes.any,
};

InputComponent.defaultProps = {
  reff: undefined,
  toggleEditComponent: undefined,
  placeholder: null,
  styleType: "material",
  TrailingIcon: null,
  LeadingIcon: null,
  colorType: "primary",
  width: null,
  meta: { error: false, success: false, submitError: undefined },
  outlineType: "solid",
  onClick: () => {},
  message: "",
  disabled: false,
  label: null,
  loading: false,
  showPassword: false,
  inputLimit: null,
  bigFont: false,
  input: {
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    name: undefined,
  },
  hideLimitCounter: false,
  trailingIconOnClick: () => {},
  theme: { primary: null },
  type: "text",
  inputMode: "text",
  autoComplete: "off",
  id: undefined,
  className: "",
  showRequiredAsterisk: false,
  readOnly: false,
  customErrors: [],
  onInput: null,
};
